/** @format */

import {
	AfterViewChecked,
	ChangeDetectorRef,
	Component,
	ElementRef,
	Inject,
	Input,
	OnInit,
	PLATFORM_ID,
	ViewChild
} from '@angular/core';

import { DeviceService } from '@shared/utils/services';
import { isPlatformBrowser } from '@angular/common';
import { BreakPoint, BreakPointType } from '@shared/data-access/enums';
import { ProTipsDataService } from './pro-tips-data.service';
import { Chain } from '@shared/data-access/interfaces';

@Component({
	selector: 'shared-component-pro-tips',
	templateUrl: './pro-tips.component.html',
	styleUrl: './pro-tips.component.scss',
	standalone: false
})
export class ProTipsComponent implements OnInit, AfterViewChecked {
	@Input() category = '';
	@ViewChild('proTipsCarousel') proTipsCarousel: ElementRef;

	articleData: any[] = [];
	carouselIndicators = [];
	isLeftBtn = false;
	isRightBtn = true;
	slidesToShow = 4;
	indicatorLength = 0;
	currentIndicatorPosition = 0;
	checked = false;
	headerSvg = '';
	altSvgText = '';

	constructor(
		@Inject(PLATFORM_ID) protected platformId: any,
		private readonly deviceService: DeviceService,
		private readonly proTipsDataService: ProTipsDataService,
		private readonly cdr: ChangeDetectorRef,
		private readonly chain: Chain
	) {}

	ngOnInit(): void {
		const deviceBreakPoint = this.deviceService.getDeviceBreakPoint();
		this.proTipsDataService
			.getProTipsData$(this.category, this.chain.chainIdentifierAbbr)
			.subscribe((res) => {
				if (res) {
					this.articleData = res;
					this.slidesToShow = 4;
					if (deviceBreakPoint === BreakPoint.MEDIUM) {
						this.slidesToShow = 3;
					}
					if (
						deviceBreakPoint === BreakPoint.EXTRASMALL ||
						deviceBreakPoint === BreakPoint.SMALL
					) {
						this.slidesToShow = 2;
					}
				}
			});

		if (isPlatformBrowser(this.platformId)) {
			this.onResize(this.deviceService.getDeviceBreakPoint());
		}

		if (this.chain.chainIdentifierAbbr === 'gg') {
			this.headerSvg = 'assets/image/gg/GG_ProTips.svg';
			this.altSvgText = 'GG PRO TIPS';
		} else if (this.chain.chainIdentifierAbbr === 'pl') {
			this.headerSvg = 'assets/image/pl/PL_ProTips.svg';
			this.altSvgText = 'PL PRO TIPS';
		} else {
			this.headerSvg = 'assets/image/dsg/Dicks_ProTips.svg';
			this.altSvgText = 'DSG PRO TIPS';
		}
	}

	ngAfterViewChecked(): void {
		if (!this.checked && this.articleData?.length !== 0 && this.proTipsCarousel !== undefined) {
			if (isPlatformBrowser(this.platformId)) {
				this.proTipsCarousel.nativeElement.style.setProperty(
					'--articleDataLength',
					this.articleData.length
				);
			}
			this.updateIndicatorLength();
			this.checked = true;
			this.cdr.detectChanges();
		}
	}

	updateIndicatorLength(): void {
		if (this.proTipsCarousel !== undefined) {
			this.proTipsCarousel.nativeElement.scrollLeft = 0;
		}
		this.indicatorLength = Math.ceil(this.articleData.length / this.slidesToShow);
		this.carouselIndicators = new Array(this.indicatorLength).fill(0);
		this.currentIndicatorPosition = 0;
	}

	moveCarouselItem(dir: string): void {
		const carouselItem =
			this.proTipsCarousel.nativeElement.querySelectorAll('.pro-tips-carousel-item');
		const carouselItemWidth = carouselItem[0].clientWidth;
		const extraPadding = this.deviceService.getDeviceBreakPoint() > BreakPoint.MEDIUM ? 25 : 0;
		if (dir === 'left') {
			if (
				this.currentIndicatorPosition === this.indicatorLength - 1 &&
				this.articleData.length % this.slidesToShow !== 0
			) {
				this.proTipsCarousel.nativeElement.scrollTo({
					left:
						this.proTipsCarousel.nativeElement.scrollLeft -
						((this.articleData.length % this.slidesToShow) * carouselItemWidth +
							extraPadding),
					behavior: 'smooth'
				});
			} else {
				this.proTipsCarousel.nativeElement.scrollTo({
					left:
						this.proTipsCarousel.nativeElement.scrollLeft -
						(this.slidesToShow * carouselItemWidth + extraPadding),
					behavior: 'smooth'
				});
			}
		} else {
			this.proTipsCarousel.nativeElement.scrollTo({
				left:
					this.proTipsCarousel.nativeElement.scrollLeft +
					(this.slidesToShow * carouselItemWidth + extraPadding),
				behavior: 'smooth'
			});
		}
	}

	updateIndicatorPosition(): void {
		this.cdr.detectChanges();
		const currentPos = this.proTipsCarousel.nativeElement.getBoundingClientRect();
		const carouselItem =
			this.proTipsCarousel.nativeElement.querySelectorAll('.pro-tips-carousel-item');
		const elementPosArr = [];
		for (let i = 0; i < this.articleData.length; i++) {
			elementPosArr.push(carouselItem[i].getBoundingClientRect().x);
		}
		let elementOnScreen: number;
		for (let i = 0; i < elementPosArr.length; i++) {
			if (Math.abs(Math.abs(currentPos.x) - Math.abs(elementPosArr[i])) < 50) {
				elementOnScreen = i;
				break;
			}
		}

		if (this.articleData.length % this.slidesToShow !== 0) {
			if (elementOnScreen >= this.articleData.length - this.slidesToShow) {
				this.currentIndicatorPosition = this.indicatorLength - 1;
			} else {
				if (elementOnScreen % this.slidesToShow !== 0) {
					return;
				}
				this.currentIndicatorPosition = elementOnScreen / this.slidesToShow;
			}
		} else {
			if (elementOnScreen % this.slidesToShow !== 0) {
				return;
			}
			this.currentIndicatorPosition = elementOnScreen / this.slidesToShow;
		}
	}

	onResize(deviceBreakPoint: BreakPointType): void {
		const defaultSlidesId = 3;

		// Map of breakpoints and slides to show.
		const slides = {
			[BreakPoint.EXTRASMALL]: 2,
			[BreakPoint.SMALL]: 2,
			[BreakPoint.MEDIUM]: 3,
			3: 4
		};

		const newSlides = slides[deviceBreakPoint] ?? slides[defaultSlidesId];

		if (newSlides != this.slidesToShow) {
			this.slidesToShow = newSlides;
			this.updateIndicatorLength();
		}
	}

	trackByUrl(index: number, item: any): string {
		return index.toString() + item.url;
	}

	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	trackByFn(index: number, _item: any): number {
		return index;
	}
}
